import {
  colorFromEmail,
  sb,
  roleColor,
  roleLabel,
  roleOf,
  allRoles
} from '../functions/index.js'

// Per-user role assignments live in `s.root.roles.byUser` (loaded from
// GET /auth/org/:orgId/member-roles on sign-in — CU-10 §90). All access is
// funneled through the helpers in functions/index.js so the underlying
// shape can be reshaped without touching components.

// Child keys are prefixed `Pc` so they don't collide with framework
// component names (Avatar, Tag, Title) which would otherwise be auto-
// extended by the global registry and silently break the render.
export const PersonCard = {
  extends: 'Link',
  href: (el, s) => `/profile?u=${encodeURIComponent(s.email || '')}`,
  flow: 'y',
  gap: 'B',
  padding: 'B',
  round: 'C',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'title',
  ':hover': { borderColor: 'blue' },

  PcHeader: {
    flow: 'x',
    align: 'center',
    gap: 'A',
    marginTop: 'Z',

    PcAvatar: {
      align: 'center center',
      width: 'D',
      height: 'D',
      round: '50%',
      color: 'white',
      fontSize: 'B',
      fontWeight: '800',
      background: (el, s) => colorFromEmail(s.email),
      text: (el, s) => s.avatar || ''
    },

    PcName: {
      flow: 'y',
      gap: 'Y',
      flex: '1',
      PcTitle: {
        tag: 'span',
        fontSize: 'B',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => s.name
      },
      PcPosition: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => s.position
      }
    }
  },

  PcDept: {
    tag: 'span',
    alignSelf: 'flex-start',
    padding: 'Y A',
    round: 'B',
    fontSize: 'Y2',
    fontWeight: '700',
    color: 'title',
    background: 'overlayStrong',
    border: 'none',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    text: (el, s) => {
      const email = String(s.email || '').toLowerCase()
      const teams = s.root?.teamsByEmail?.[email] || []
      return teams[0]?.name || 'Member'
    }
  },

  PcMeta: {
    flow: 'y',
    gap: 'Z',
    fontSize: 'Z2',
    color: 'paragraph',
    PcLoc: { tag: 'span', text: (el, s) => `${s.location || '—'}` },
    PcTz: { tag: 'span', color: 'caption', text: (el, s) => s.timezone || '' },
    PcMail: { tag: 'span', color: 'caption', text: (el, s) => s.email }
  },

  // Read-only role badge. Editing happens in the dedicated Role manager
  // panel on the People page (superadmin only).
  PcRole: {
    hide: (el) => !el.call('isSuperAdmin'),
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    paddingTop: 'A',

    PcRoleLabel: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      text: 'Role'
    },

    PcRoleBadge: {
      tag: 'span',
      padding: 'Y A',
      round: 'B',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'white',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      background: (el, s) => roleColor(s, roleOf(s, s.userId) || 'member'),
      text: (el, s) => roleLabel(s, roleOf(s, s.userId) || 'member')
    }
  }
}

// Per-row role editor used inside the Role manager panel on the People
// page. Renders a colored role chip + name on the left and a `<select>`
// on the right that upserts to public.user_roles when changed.
export const RoleManagerRow = {
  flow: 'x',
  align: 'center space-between',
  gap: 'B',
  padding: 'Z A',
  round: 'C',
  ':hover': { background: 'overlayStrong' },

  RmLeft: {
    flow: 'x',
    align: 'center',
    gap: 'A',
    flex: '1',
    minWidth: '0',
    RmDot: {
      width: 'Z',
      height: 'Z',
      round: '50%',
      flex: '0 0 auto',
      background: (el, s) => roleColor(s, roleOf(s, s.userId) || 'member')
    },
    RmName: {
      flow: 'y',
      gap: 'Y',
      flex: '1',
      minWidth: '0',
      RmNameTitle: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => s.name
      },
      RmNameSub: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => s.email
      }
    }
  },

  RmSelect: {
    tag: 'select',
    padding: 'Y A',
    round: 'B',
    background: 'overlayStrong',
    border: 'none',
    color: 'title',
    fontSize: 'Z2',
    fontWeight: '700',
    cursor: 'pointer',
    onChange: async (ev, el, s) => {
      const newRole = ev.target.value
      if (!s.userId) return
      const prev = s.root.roles || { available: [], byUser: {}, version: 0 }
      s.root.update({
        roles: {
          ...prev,
          byUser: { ...(prev.byUser || {}), [s.userId]: newRole },
          version: (prev.version || 0) + 1
        }
      })
      try {
        await sb()
          .from('user_roles')
          .upsert(
            { user_id: s.userId, role: newRole },
            { onConflict: 'user_id' }
          )
      } catch (e) {
        console.warn('[intranet] role update failed', e)
      }
    },
    onRender: (el, s) => {
      if (!el.node) return
      const fill = () => {
        el.node.innerHTML = ''
        const current = roleOf(s, s.userId) || 'member'
        const roles = allRoles(s)
        const list = roles.length
          ? roles
          : [{ name: 'member', label: 'Member' }]
        for (const r of list) {
          const opt = document.createElement('option')
          opt.value = r.name
          opt.textContent = r.label || r.name
          if (r.name === current) opt.selected = true
          el.node.appendChild(opt)
        }
      }
      fill()
      // The roles fetch may resolve after first render. Re-fill once
      // more when the data lands.
      let tries = 0
      const t = setInterval(() => {
        if (allRoles(s).length) {
          fill()
          clearInterval(t)
          return
        }
        if (++tries > 10) clearInterval(t)
      }, 200)
      el._rmTimer = t
    },
    onRemove: (el) => {
      if (el._rmTimer) clearInterval(el._rmTimer)
    }
  }
}

export const DocumentRow = {
  extends: 'Link',
  href: (el, s) => s.url || '/documents',
  textDecoration: 'none',
  color: 'inherit',
  flow: 'x',
  align: 'center',
  gap: 'B',
  padding: 'B 0',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  cursor: 'pointer',
  ':hover': { background: 'overlayStrong' },

  DrIcon: {
    extends: 'Icon',
    icon: 'document',
    boxSize: 'C',
    color: 'blue'
  },

  DrBody: {
    flow: 'y',
    gap: 'Y',
    flex: '1',
    minWidth: '0',
    DrTitle: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.title
    },
    DrMeta: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => {
        const parts = []
        if (s.type) parts.push(s.type)
        if (s.size) parts.push(s.size)
        if (s.signedAt) parts.push(`signed ${s.signedAt}`)
        return parts.join(' · ')
      }
    }
  },

  DrAction: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'blue',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    text: 'Open'
  }
}

// Sidebar filter row on the People page. State carries `kind` ('group' |
// 'role'), `name` (the value to filter by, null = clear), `label`, and
// optional `count`. Click toggles the matching root-state filter.
// Resolves which `root.selected*` field corresponds to a given filter kind.
const _selField = (kind) => {
  if (kind === 'role') return 'selectedRole'
  if (kind === 'category') return 'selectedTeamCategory'
  return 'selectedGroup'
}

export const PeopleFilterLink = {
  tag: 'button',
  flow: 'x',
  align: 'center space-between',
  gap: 'A',
  padding: 'Z A',
  round: 'C',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  background: (el, s) => s.root[_selField(s.kind)] === s.name ? 'blue.2' : 'transparent',
  color: (el, s) => s.root[_selField(s.kind)] === s.name ? 'title' : 'caption',
  fontWeight: (el, s) => s.root[_selField(s.kind)] === s.name ? '700' : '500',
  ':hover': { color: 'title' },
  onClick: (ev, el, s) => {
    ev.preventDefault()
    s.root.update({ [_selField(s.kind)]: s.name })
  },
  PflLabel: { tag: 'span', fontSize: 'Z2', text: (el, s) => s.label },
  PflCount: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => (s.count != null ? String(s.count) : '')
  }
}

export const CompanyInfoRow = {
  flow: 'y',
  gap: 'Z',
  padding: 'B 0',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  CiLabel: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'caption',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    text: (el, s) => s.label || s.key
  },
  CiValue: {
    tag: 'span',
    fontSize: 'B',
    color: 'title',
    lineHeight: '1.55',
    text: (el, s) => s.value
  }
}

export const PeopleGroupBlock = {
  flow: 'y',
  gap: 'B',

  PgHead: {
    flow: 'x',
    align: 'baseline',
    gap: 'A',
    paddingBottom: 'Z',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    PgTitle: {
      tag: 'h2',
      fontSize: 'C',
      fontWeight: '800',
      color: 'title',
      letterSpacing: '-0.01em',
      text: (el, s) => s.name
    },
    PgCount: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => `${(s.people || []).length}`
    }
  },

  PgGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 'B',
    childExtends: 'PersonCard',
    childrenAs: 'state',
    children: (el, s) => s.people || []
  }
}
